import { useState, useEffect, useCallback, useRef } from "react";

import {
  ProjectDevelopmentMetrics,
  ProjectDevelopmentMetricsDetails,
  ProjectMetricsFromJSON,
} from "../bitmaker/schemas/Projects/ProjectMetrics";
import { BitmakerAPIClient } from "../services/BitmakerApp";
import { useApiError } from "./useApiError";

export const useProjectDevelopmentMetrics = (
  projectId: number,
  fromDate: Date,
  toDate: Date,
) => {
  const [stats, setStats] = useState<ProjectDevelopmentMetrics | null>(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [fetchMetricsError, setFetchMetricsError] = useState<string | null>(
    null,
  );

  const { handleAPIError } = useApiError();
  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const cleanEmptyChildren = useCallback(
    (
      items: ProjectDevelopmentMetricsDetails[],
    ): ProjectDevelopmentMetricsDetails[] => {
      return items.map(({ children, ...rest }) =>
        children && children.length > 0
          ? { ...rest, children: cleanEmptyChildren(children) }
          : { ...rest, children: [] },
      );
    },
    [],
  );

  const getExpandedKeys = useCallback(
    (details: ProjectDevelopmentMetricsDetails[]): string[] => {
      const keys: string[] = [];

      const walk = (items: ProjectDevelopmentMetricsDetails[]) => {
        items.forEach((item) => {
          keys.push(item.id.toString());
          if (item.children?.length) walk(item.children);
        });
      };

      walk(details);
      return keys;
    },
    [],
  );

  const fetchMetrics = useCallback(() => {
    if (!projectId || !isMountedRef.current) return;

    setLoadingMetrics(true);
    setFetchMetricsError(null);

    BitmakerAPIClient.get(
      `/projects/${projectId}/metrics`,
      {
        from: fromDate.toISOString().slice(0, 10),
        to: toDate.toISOString().slice(0, 10),
      },
      (response: { data: ProjectDevelopmentMetrics }) => {
        if (!isMountedRef.current) return;

        const { isError, message } = handleAPIError(response);

        if (isError) {
          console.error("Error fetching development metrics:", message);
          setFetchMetricsError(
            "Error fetching development metrics. Please try again.",
          );
          setLoadingMetrics(false);
          return;
        }

        const cleanedDetails = cleanEmptyChildren(response.data.details || []);

        setStats({
          ...ProjectMetricsFromJSON(response.data),
          details: cleanedDetails,
        });

        setExpandedRowKeys(getExpandedKeys(cleanedDetails));
        setLoadingMetrics(false);
      },
    );
  }, [
    projectId,
    fromDate,
    toDate,
    handleAPIError,
    cleanEmptyChildren,
    getExpandedKeys,
  ]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const handleExpand = useCallback(
    (expanded: boolean, record: ProjectDevelopmentMetricsDetails) => {
      const id = record.id.toString();

      setExpandedRowKeys((prev) =>
        expanded
          ? Array.from(new Set([...prev, id]))
          : prev.filter((key) => key !== id),
      );
    },
    [],
  );

  return {
    stats,
    expandedRowKeys,
    loadingMetrics,
    fetchMetricsError,
    handleExpand,
    refetchMetrics: fetchMetrics,
  };
};
